import React, { useMemo, useRef, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ReactWordcloud from "react-wordcloud";
import type { DistributionItem } from "../../../shared/api/types";
import { formatChartTitle } from "../../utils";

interface WordCloudProps {
  data: DistributionItem[];
  title?: string;
  height?: number;
}

export const WordCloud: React.FC<WordCloudProps> = ({
  data,
  title,
  height = 300,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(400);
  const formattedTitle = formatChartTitle(title);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const words = useMemo(
    () =>
      data.map((item) => ({
        text: item.label,
        value: item.value,
      })),
    [data],
  );

  const fontSizeRange = useMemo(() => {
    const count = words.length;
    const maxBase = Math.max(42, Math.round(height * 0.28));
    const minBase = Math.max(18, Math.round(height * 0.14));

    if (count <= 2) return [minBase + 10, maxBase + 24];
    if (count <= 5) return [minBase + 6, maxBase + 14];
    if (count <= 8) return [minBase + 2, maxBase + 6];
    if (count <= 14) return [minBase, maxBase];
    return [Math.max(14, minBase - 6), Math.max(32, maxBase - 10)];
  }, [words.length, height]);

  const options = useMemo(
    () => ({
      rotations: 1,
      rotationAngles: [0, 0] as [number, number],
      fontSizes: fontSizeRange as [number, number],
      padding: words.length <= 6 ? 2 : 1,
      spiral: "rectangular" as const,
      random: () => 0.5,
      fontFamily: "Arial",
    }),
    [fontSizeRange, words.length],
  );

  return (
    <Box>
      {formattedTitle && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {formattedTitle}
        </Typography>
      )}
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: height,
          padding: 4,
        }}
      >
        {width > 0 && words.length > 0 ? (
          <ReactWordcloud
            words={words}
            options={options}
            size={[width, height]}
            minSize={[200, 200]}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No terms available.
          </Typography>
        )}
      </Box>
    </Box>
  );
};
